document.addEventListener("alpine:init", () => {
  Alpine.data("freshDesk", () => ({
    // Local UI state used by the template
    deliveryAddressDiff: false,
    businessType: "",

    async init() {
      const form = document.querySelector("#fresh-desk-form");
      const messages = document.querySelector("#fresh-desk-messages");

      form.addEventListener("submit", async function (e) {
        e.preventDefault();
        messages.innerHTML = "";

        // Use native constraint validation so all required/pattern rules apply
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        // Ensure terms and conditions are accepted/checked
        const acceptTerms = form.querySelector(
          "input[name='freshDesk[accept_terms]']",
        );
        if (acceptTerms && !acceptTerms.checked) {
          messages.innerHTML = "<p>Please accept the terms and conditions.</p>";
          return;
        }

        // Prevent users from submitting outside of the whitelist of countries
        const countriesBlacklist = ["US", "CA"];
        const tradeCountry = form.querySelector(
          "select[name='freshDesk[trade_country]']",
        )?.value;
        const deliveryCountry = form.querySelector(
          "select[name='freshDesk[delivery_country]']",
        )?.value;

        if (tradeCountry && countriesBlacklist.includes(tradeCountry)) {
          messages.innerHTML = `<p>Trade country "${tradeCountry}" is not supported.</p>`;
          return;
        }
        if (deliveryCountry && countriesBlacklist.includes(deliveryCountry)) {
          messages.innerHTML = `<p>Delivery country "${deliveryCountry}" is not supported.</p>`;
          return;
        }

        // Ensure EORI numbers are compliant
        const eoriNum = form.querySelector(
          "input[name='freshDesk[eori_num]']",
        )?.value;
        if (eoriNum) {
          const prefix = document.querySelector(
            "#trade-form__trade_country",
          ).value;
          const eoriPattern = new RegExp(`^${prefix}\\d{8,15}$`); // Prefix followed by 8 to 15 digits
          if (!eoriPattern.test(eoriNum)) {
            messages.innerHTML = `<p>EORI Number "${eoriNum}" is not valid. It should start with "${prefix}" followed by 8 to 15 digits.</p>`;
            return;
          }
        }

        // Gather form data
        const formData = new FormData(form);
        const data = {};
        formData.forEach((value, key) => {
          data[key] = value;
        });

        // Send to FreshDesk proxy
        try {
          const freshdeskUrl =
            "https://freshdesk-proxy-282561298653.europe-west1.run.app";

          const rawPayload = Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
              key.replace(/^freshDesk\[(.*)\]$/, "$1"),
              value,
            ]),
          );

          // HTML-safe helpers
          const escapeHtml = (str) =>
            String(str)
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
              .replace(/"/g, "&quot;")
              .replace(/'/g, "&#39;");

          // Build a HTML section with non-empty values as a definition list
          const buildSectionHtml = (title, entries) => {
            const items = entries.filter(
              ([, v]) => v != null && String(v).trim() !== "",
            );
            if (!items.length) return null;
            const dl = items
              .map(
                ([label, v]) =>
                  `<dt>${escapeHtml(label)}</dt><dd>${escapeHtml(v)}</dd>`,
              )
              .join("");
            return `<h3>${escapeHtml(title)}</h3><dl>${dl}</dl>`;
          };

          // Derive grouped sections
          const businessSection = buildSectionHtml("Business Information", [
            ["Incorporated name", rawPayload.incorporated_name],
            ["Company registration number", rawPayload.company_reg_num],
            ["VAT registration number", rawPayload.vat_reg_num],
            ["UKIMS Number", rawPayload.ukims_number],
            ["EORI Number", rawPayload.eori_num],
            ["Website", rawPayload.website_url],
            ["Date established", rawPayload.date_estab],
            ["Business Type", rawPayload.business_type],
            ["Business Type (Other)", rawPayload.business_type_other],
          ]);

          const invoiceSection = buildSectionHtml("Invoice Address", [
            ["Invoice name", rawPayload.invoice_name],
            ["Address 1", rawPayload.trade_address_1],
            ["Address 2", rawPayload.trade_address_2],
            ["City", rawPayload.trade_city],
            ["County", rawPayload.trade_county],
            ["Postcode", rawPayload.trade_postcode],
            ["Country", rawPayload.trade_country],
            ["Contact Number", rawPayload.trade_contact_number],
            ["what3words", rawPayload.what3words],
          ]);

          const deliverySection =
            rawPayload.delivery_address_different === "true"
              ? buildSectionHtml("Delivery Address", [
                  ["Address 1", rawPayload.delivery_address_1],
                  ["Address 2", rawPayload.delivery_address_2],
                  ["City", rawPayload.delivery_city],
                  ["County", rawPayload.delivery_county],
                  ["Postcode", rawPayload.delivery_postcode],
                  ["Country", rawPayload.delivery_country],
                  ["Contact Number", rawPayload.delivery_contact_number],
                ])
              : null;

          const contactsSection = buildSectionHtml("Contacts", [
            [
              "Primary Contact Name",
              `${rawPayload.primary_contact_first_name || ""} ${rawPayload.primary_contact_last_name || ""}`.trim(),
            ],
            ["Primary Contact Job Title", rawPayload.primary_contact_job_title],
            ["Primary Contact Email", rawPayload.primary_contact_email],
            [
              "Sales Contact Name",
              `${rawPayload.sales_contact_first_name || ""} ${rawPayload.sales_contact_last_name || ""}`.trim(),
            ],
            ["Sales Contact Job Title", rawPayload.sales_contact_job_title],
            ["Sales Contact Email", rawPayload.sales_contact_email],
            [
              "Accounts Contact Name",
              `${rawPayload.accounts_contact_first_name || ""} ${rawPayload.accounts_contact_last_name || ""}`.trim(),
            ],
            [
              "Accounts Contact Job Title",
              rawPayload.accounts_contact_job_title,
            ],
            ["Accounts Contact Email", rawPayload.accounts_contact_email],
            [
              "Shipping Contact Name",
              `${rawPayload.shipping_contact_first_name || ""} ${rawPayload.shipping_contact_last_name || ""}`.trim(),
            ],
            ["Shipping Contact Email", rawPayload.shipping_contact_email],
            [
              "Marketing Contact Name",
              `${rawPayload.marketing_contact_first_name || ""} ${rawPayload.marketing_contact_last_name || ""}`.trim(),
            ],
            ["Marketing Contact Email", rawPayload.marketing_contact_email],
          ]);

          const notesSection = buildSectionHtml("Notes", [
            ["Notes", rawPayload.notes],
            ["Heard about us (Exhibition)", rawPayload.hear_of_us_exhibition],
            ["Heard about us (Other)", rawPayload.hear_of_us_other],
            ["Accepted Terms", rawPayload.accept_terms],
          ]);

          const descriptionBlocks = [
            "Trade Account Application Details:",
            businessSection,
            invoiceSection,
            deliverySection,
            contactsSection,
            notesSection,
          ].filter(Boolean);

          const description = [
            `<p><strong>Trade Account Application Details</strong></p>`,
            ...descriptionBlocks,
          ]
            .filter(Boolean)
            .join("<hr>");

          const subject = `Trade Account Application - ${
            rawPayload.incorporated_name ||
            rawPayload.invoice_name ||
            rawPayload.primary_contact_last_name ||
            "New"
          }`.trim();

          const email =
            rawPayload.primary_contact_email || rawPayload.email || "";
          const name =
            `${(rawPayload.primary_contact_first_name || "").trim()} ${(rawPayload.primary_contact_last_name || "").trim()}`.trim();

          const payload = {
            email: email || undefined,
            name: name || undefined,
            subject,
            description,
            type: "Trade Application", // must match value configured in Freshdesk
            tags: ["trade-account", "trade-application", "website-form"],
          };

          const response = await fetch(freshdeskUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          // mock response for testing, returns ok
          // const response = await fetch("https://httpbingo.org/status/200", {
          //   method: "POST",
          //   headers: {
          //     "Content-Type": "application/json",
          //   },
          //   body: JSON.stringify(payload),
          // });

          if (response.ok) {
            const tradeForm = document.querySelector(".trade-account-form");
            tradeForm.innerHTML = `
              <div style="text-align: center;">
                <h2 class="header-lg-bold form__message-title">Application submitted</h2>
                <p class="copy-medium form__message-copy">
                Thank you for submitting your trade application form. We will be in touch shortly.
                </p>
              </div>
            `;
            tradeForm.scrollIntoView({ behavior: "smooth", block: "center" });
          } else {
            const errorText = await response.text();
            messages.innerHTML = `<p>There was an error: ${errorText || ""}</p>`;
          }
        } catch (err) {
          messages.innerHTML = "<p>Network error. Please try again later.</p>";
        }
      });
    },
  }));
});
